import {
  RtFraction,
  RtJsonDate,
  RtPositiveInteger,
  RtRichTextData,
  RtWithName,
} from "@aprao/common/src/runtypes/common.js";
import { RtCurrencyCode } from "@aprao/common/src/runtypes/RtCurrency.js";
import { RtLengthUnitCode } from "@aprao/common/src/runtypes/RtLengthUnit.js";
import { z } from "zod";

import { RtAppraisal, RtAppraisalSnippet } from "./RtAppraisal.js";

export const RtBoardLevel = z.enum(["personal", "team"]);
export const RtBoard = z.strictObject({
  id: z.number(),
  version: z.number(),
  label: z.string(),
  isSample: z.boolean(),
  boardLevel: RtBoardLevel,
});

// fields generated managed by the database (cannot be updated by the user)
const fieldsSynthetic = {
  id: z.number(),
  createdDate: RtJsonDate,
  version: z.number(),
};

export const RtSalesTaxConfiguration = RtWithName.extend({
  value: RtFraction,
  isMarginScheme: z.boolean(),
});

export const RtProjectDocument = z.strictObject({
  siteAddress: z.string(),
  summary: RtRichTextData,
  currencyCode: RtCurrencyCode,
  lengthUnitCode: RtLengthUnitCode,
});

const RtProjectDocumentRelated = z.strictObject({
  document: RtProjectDocument,
});

export const RtProjectMove = z.strictObject({
  boardListId: z.number(),
  rank: RtPositiveInteger,
});

export const RtProjectSend = z.strictObject({
  boardId: z.number(),
});

export const RtProjectCreate = RtProjectDocumentRelated.extend({
  rank: RtPositiveInteger,
});

const ProjectTemplateVisibilitySchema = z.enum(["public", "private"]);
export const ProjectTemplateVisibilityPatchSchema = z.object({
  visibility: ProjectTemplateVisibilitySchema,
});

export const RtReportConfiguration = z.object({
  isMapEnabled: z.boolean(),
});

export const RtProjectPatch = RtProjectDocumentRelated.extend({
  salesTaxConfiguration: RtSalesTaxConfiguration.nullable(),
  reportConfiguration: RtReportConfiguration,
}).partial();

export const RtProject = RtProjectDocumentRelated.extend({
  ...fieldsSynthetic,
  primaryAppraisal: z.object({ id: z.number() }).nullable(),
  board: z.object({ id: z.number() }),
  isTemplate: z.boolean(),
  // can be null when the project is not a template
  templateVisibility: ProjectTemplateVisibilitySchema.nullable(),
  reportConfiguration: RtReportConfiguration,
  salesTaxConfiguration: RtSalesTaxConfiguration.nullable(),
});

export const RtProjectExpanded = RtProject.extend({
  board: RtBoard,
});

export const RtProjectEmbed = RtProjectExpanded.extend({
  appraisals: z.array(RtAppraisalSnippet),
});

export const RtAppraisalExpanded = RtAppraisal.extend({
  project: RtProjectExpanded,
});

export const RtProjectsSearchResult = z.object({
  limit: z.number(),
  items: z.array(RtProjectExpanded),
});
